import { FormEvent, useEffect, useState } from 'react';
import { uploadImage } from '../api/client';
import { PhotoInfo } from '../api/types';
import { Button } from './components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/dialog';
import { Input } from './components/input';
import { Label } from './components/label';
import { Switch } from './components/switch';

export interface PhotoFormValues {
  image_url: string;
  caption_en: string;
  caption_tr: string;
  sort_order: number;
  is_active: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: PhotoInfo | null;
  onSubmit: (values: PhotoFormValues) => Promise<void>;
}

const emptyValues: PhotoFormValues = {
  image_url: '',
  caption_en: '',
  caption_tr: '',
  sort_order: 0,
  is_active: true,
};

function PhotoForm({ open, onOpenChange, initial, onSubmit }: Props) {
  const [values, setValues] = useState<PhotoFormValues>(initial ?? emptyValues);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setValues(initial ?? emptyValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = <K extends keyof PhotoFormValues>(key: K, val: PhotoFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { url } = await uploadImage(file);
      set('image_url', url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Yükleme başarısız');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!values.image_url) {
      setError('Lütfen bir fotoğraf yükleyin');
      return;
    }

    setSaving(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) { setValues(initial ?? emptyValues); onOpenChange(o); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? 'Fotoğrafı Düzenle' : 'Yeni Fotoğraf'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Fotoğraf</Label>
            {values.image_url && (
              <img src={values.image_url} alt="" className="h-32 w-32 rounded object-cover" />
            )}
            <Input type="file" accept="image/*" onChange={handleFileChange} disabled={uploading} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="caption_en">Açıklama (EN)</Label>
              <Input id="caption_en" value={values.caption_en} onChange={(e) => set('caption_en', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="caption_tr">Açıklama (TR)</Label>
              <Input id="caption_tr" value={values.caption_tr} onChange={(e) => set('caption_tr', e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sort_order">Sıra</Label>
              <Input
                id="sort_order"
                type="number"
                value={values.sort_order}
                onChange={(e) => set('sort_order', Number(e.target.value))}
                className="w-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="is_active">Aktif</Label>
              <Switch id="is_active" checked={values.is_active} onCheckedChange={(v) => set('is_active', v)} />
            </div>
          </div>

          {error && <p className="text-sm text-[hsl(var(--destructive))]">{error}</p>}

          <Button type="submit" disabled={saving || uploading}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoForm;
